import toast from "react-hot-toast";
import axiosInstance from "../../utils/axios";
import React, { useState, useEffect } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaShieldAlt,
} from "react-icons/fa";
import { FiEdit, FiSave, FiX } from "react-icons/fi";
import { MdAdminPanelSettings } from "react-icons/md";
import Swal from "sweetalert2";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";

const ChangePasswordModal = ({ userData, setShowPasswordModal }) => {
  const [passwords, setPasswords] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleInputChange = (field, value) => {
    setPasswords((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setPasswords({ oldPassword: "", newPassword: "", confirmPassword: "" });
  };

  const handleChangePassword = async () => {
    const { oldPassword, newPassword, confirmPassword } = passwords;

    if (!oldPassword || !newPassword || !confirmPassword) {
      return Swal.fire("Missing Fields", "Please fill all fields", "warning");
    }

    if (newPassword !== confirmPassword) {
      return Swal.fire(
        "Mismatch",
        "New password and confirm password must match",
        "error"
      );
    }

    try {
      const confirm = await Swal.fire({
        title: "Are you sure?",
        text: "Do you want to change your password?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, change it",
        cancelButtonText: "Cancel",
      });

      if (!confirm.isConfirmed) return;

      console.log("oldPassword:", oldPassword);
      console.log("newPassword:", newPassword);

      const resp = await axiosInstance.post(
        `/admins/change-password/${userData._id}`,
        { oldPassword, newPassword }
      );

      Swal.fire("Success", resp.data.message, "success");
      setShowPasswordModal(false);
      resetForm();
    } catch (error) {
      Swal.fire(
        "Error",
        error?.response?.data?.message || "Unable to change password",
        "error"
      );
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">
          Change Password
        </h2>

        <div className="space-y-3">
          {["oldPassword", "newPassword", "confirmPassword"].map(
            (field, idx) => (
              <input
                key={idx}
                type="password"
                placeholder={
                  field === "oldPassword"
                    ? "Old Password"
                    : field === "newPassword"
                    ? "New Password"
                    : "Confirm New Password"
                }
                value={passwords[field]}
                onChange={(e) => handleInputChange(field, e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F]"
              />
            )
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={() => setShowPasswordModal(false)}
            className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleChangePassword}
            className="px-4 py-2 bg-[#02323F] hover:bg-[#045266] text-white rounded-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const AdminProfile = () => {
  const [userData, setUserData] = useState({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const navigate = useNavigate();

  let toastId = "toast";
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("admin-data") || "{}");

    if (token) {
      setIsLoggedIn(true);
      const mockUserData = {
        name: user.name || "Admin User",
        email: user.email || "example@gmail.com",
        phoneNumber: user.phoneNumber || "+91 0000000000",
        address: user.address || "",
        ...user,
      };
      setUserData(mockUserData);
      setEditData(mockUserData);
    }
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You’ll be logged out of your admin account.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        // Show a success message after confirmation
        Swal.fire({
          icon: "success",
          title: "Logged out!",
          text: "Redirecting to login...",
          timer: 2000,
          showConfirmButton: false,
        });

        setTimeout(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("admin-data");
          setIsLoggedIn(false);
          navigate("/admin-login");
        }, 2000);
      }
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const adminId =
        userData._id || JSON.parse(localStorage.getItem("admin-data"))?._id;

      // ✅ allowed fields from backend
      const allowedUpdates = [
        "name",
        "email",
        "phoneNumber",
        "gender",
        "dateOfBirth",
        "address",
        "nationality",
        "profilePic",
      ];

      // ✅ filter editData before sending
      const filteredData = Object.keys(editData)
        .filter((key) => allowedUpdates.includes(key))
        .reduce((obj, key) => {
          obj[key] = editData[key];
          return obj;
        }, {});

      // x
      const res = await axiosInstance.patch(
        `/admins/admin-edit-profile/${adminId}`,
        filteredData
      );

      // ✅ update local state + storage
      setUserData(res.data.admin);
      setEditData(res.data.admin);
      localStorage.setItem("admin-data", JSON.stringify(res.data.admin));
      toast.success("Profile updated successfully ");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating admin:", error);
      if (error.response) {
        toast.error(error.response.data.message || "Failed to update profile");
      } else {
        toast.error("Something went wrong while updating profile.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditData(userData);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#02323F] via-[#0a4a5c] to-[#02323F] flex items-center justify-center px-4">
        <div className="text-center">
          <MdAdminPanelSettings className="text-8xl text-white/20 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">
            Admin Access Required
          </h1>
          <p className="text-gray-300 mb-8">
            Please log in with admin credentials to access this page
          </p>
          <button
            onClick={() => (window.location.href = "/admin-login")}
            className="bg-[#FCC101] hover:bg-[#e6b800] text-white font-semibold px-8 py-3 rounded-xl transition-colors"
          >
            Go to Admin Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-auto bg-gray-50">
      {showPasswordModal && (
        <ChangePasswordModal
          userData={userData}
          setShowPasswordModal={setShowPasswordModal}
        />
      )}
      <div className="w-full mx-auto px-4 py-8">
        {/* Profile Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Admin Profile</h2>

            <div className="flex gap-2">
              {!isEditing ? (
                <button
                  onClick={handleEdit}
                  className="flex items-center gap-2 bg-[#02323F] hover:bg-[#034557] text-white font-medium py-2 px-4 rounded-lg transition-colors"
                >
                  <FiEdit className="text-sm" />
                  Edit Profile
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FiSave className="text-sm" />
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex items-center gap-2 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    <FiX className="text-sm" />
                    Cancel
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                <FiEdit className="text-sm" />
                Logout
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <FaUser className="text-xs" />
                Full Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.name || ""}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                />
              ) : (
                <p className="text-lg font-semibold text-gray-800 bg-gray-50 p-3 rounded-lg">
                  {userData.name}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <FaEnvelope className="text-xs" />
                Email Address
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={editData.email || ""}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                />
              ) : (
                <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-lg break-words">
                  {userData.email}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <FaPhone className="text-xs" />
                Phone Number
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editData.phoneNumber || ""}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                />
              ) : (
                <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {userData.phoneNumber}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-2">
                <FaMapMarkerAlt className="text-xs" />
                Address
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editData.address || ""}
                  onChange={(e) => handleInputChange("address", e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#02323F] focus:border-transparent outline-none transition-all"
                />
              ) : (
                <p className="text-lg font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">
                  {userData.address}
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FaShieldAlt className="text-[#02323F]" />
              Security Settings
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FaShieldAlt className="text-blue-600" />
                </div>
                <div>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="font-medium text-gray-800"
                  >
                    Change Password
                  </button>
                  <p className="text-sm text-gray-600">
                    Update your login password
                  </p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;
